import { FC, useContext, useEffect, useState } from 'react'
import { Box, IconButton, List, ListItem, ListItemText, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { PlaybookContext } from '../../../../contexts/playbooks.context'
import { useToast } from '../../../../contexts/toast.context'
import { IPlaybookReport } from '../../../../../shared/types/api/playbook.type'
import {
  getPlaybookReports,
  createPlaybookReport,
  updatePlaybookReport,
  deletePlaybookReport,
} from '../../../../services/playbook-report.service'
import PlaybookReportModal from './PlaybookReportModal'
import PlaybookRunReportModal from './PlaybookRunReportModal'
import ConfirmationModal from '../../Modals/ConfirmationModal'

import styles from './styles.module.scss'

const PlaybookReportsPanel: FC = () => {
  const { playbook } = useContext(PlaybookContext)
  const { showToast } = useToast()
  const [reports, setReports] = useState<IPlaybookReport[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [editingReport, setEditingReport] = useState<IPlaybookReport | null>(null)
  const [deletingReport, setDeletingReport] = useState<IPlaybookReport | null>(null)
  const [runningReport, setRunningReport] = useState<IPlaybookReport | null>(null)

  const fetchReports = async () => {
    if (!playbook) return
    const resp = await getPlaybookReports(playbook.id)
    if (resp.success && resp.data) {
      setReports(resp.data)
    }
  }

  useEffect(() => {
    fetchReports()
  }, [playbook?.id])

  const handleAdd = () => {
    setEditingReport(null)
    setModalOpen(true)
  }

  const handleEdit = (report: IPlaybookReport) => {
    setEditingReport(report)
    setModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!playbook || !deletingReport) return
    try {
      const resp = await deletePlaybookReport(playbook.id, deletingReport.id)
      if (resp.success) {
        setReports((prev) => prev.filter((r) => r.id !== deletingReport.id))
      }
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to delete report')
    }
    setDeletingReport(null)
  }

  const handleSave = async (body: { name: string; slug: string; query: string; params: string[] }) => {
    if (!playbook) return

    try {
      if (editingReport) {
        const resp = await updatePlaybookReport(playbook.id, editingReport.id, body)
        if (resp.success && resp.data) {
          setReports((prev) => prev.map((r) => (r.id === editingReport.id ? resp.data! : r)))
        } else {
          showToast('error', resp.message)
          return
        }
      } else {
        const resp = await createPlaybookReport(playbook.id, body)
        if (resp.success && resp.data) {
          setReports((prev) => [...prev, resp.data!])
        } else {
          showToast('error', resp.message)
          return
        }
      }
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to save report')
      return
    }

    setModalOpen(false)
  }

  return (
    <Box className={styles.panel}>
      <Box className={styles.header}>
        <Typography variant="subtitle1" fontWeight={600}>
          Reports
        </Typography>
        <Tooltip title="Add report" disableInteractive>
          <IconButton size="small" onClick={handleAdd} data-testid="add-report-button">
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <List dense>
        {reports.map((report) => (
          <ListItem
            key={report.id}
            secondaryAction={
              <Box>
                <IconButton
                  size="small"
                  onClick={() => setRunningReport(report)}
                  data-testid={`run-report-${report.id}`}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleEdit(report)} data-testid={`edit-report-${report.id}`}>
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setDeletingReport(report)}
                  data-testid={`delete-report-${report.id}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <ListItemText primary={report.name} secondary={report.slug} />
          </ListItem>
        ))}
      </List>
      <PlaybookReportModal
        open={modalOpen}
        report={editingReport}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
      {runningReport && playbook && (
        <PlaybookRunReportModal
          open={!!runningReport}
          report={runningReport}
          playbookSlug={playbook.slug}
          onClose={() => setRunningReport(null)}
        />
      )}
      {deletingReport && (
        <ConfirmationModal
          text={`Are you sure you want to delete "${deletingReport.name}"?`}
          btnText="Delete"
          onClose={() => setDeletingReport(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </Box>
  )
}

export default PlaybookReportsPanel
